'use client';

import { usePagesContext } from './PagesProvider';
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';

export type OpenStateItem = {
  id: string;
  close: () => void;
};

type PageLocation = {
  pathname: string;
  query: Record<string, string | string[]>;
};

export type EmbedContextProps = {
  isInIframe: boolean;
  targetAttribute: '_blank' | '_self' | '_parent' | '_top';
  headerRef: React.RefObject<HTMLDivElement | null> | null;
  registerOpenState: (items: OpenStateItem[]) => void;
  unregisterOpenState: (ids: string[]) => void;
  closeAllOpenStates: () => void;
  getPageLocation: () => PageLocation;
  navigateToRoute: (route: string) => void;
  getLoginUrl: () => string;
  reloadPage: () => void;
};

export type EmbedOverlayProps = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
};

const EmbedContext = React.createContext<EmbedContextProps>({
  isInIframe: false,
  targetAttribute: '_self',
  headerRef: null,
  registerOpenState: () => {
    /* no-op */
  },
  unregisterOpenState: () => {
    /* no-op */
  },
  closeAllOpenStates: () => {
    /* no-op */
  },
  getPageLocation: () => ({ pathname: '', query: {} }),
  navigateToRoute: () => {
    /* no-op */
  },
  getLoginUrl: () => '/login',
  reloadPage: () => {
    /* no-op */
  },
});

const DEFAULT_HEADER_HEIGHT = '50px';
const FULL_SCREEN_HEADER_HEIGHT = '100vh';

/**
 * Embed Provider Component.
 */
export const EmbedProvider = (
  props: PropsWithChildren<{
    isEmbed?: boolean;
  }>,
) => {
  const { children, isEmbed = false } = props;

  const targetAttribute = useMemo(() => {
    return isEmbed ? '_parent' : '_self';
  }, [isEmbed]);

  const headerRef = useRef<HTMLDivElement>(null);

  const isHoveredRef = useRef(false);

  // Changed from Set to Map to store the close handler associated with the ID
  const openElementsRef = useRef<Map<string, () => void>>(new Map());

  const { pathname, query, push } = usePagesContext();

  const navigateToRoute = (route: string) => {
    if (isEmbed) {
      window.parent.location.href = route;
      return;
    }
    push(route);
  };

  const getCollapsedHeaderHeight = useCallback(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return DEFAULT_HEADER_HEIGHT;

    const measuredHeight =
      headerEl.getBoundingClientRect().height || headerEl.offsetHeight;
    if (measuredHeight) return `${measuredHeight}px`;

    return headerEl.style.height || DEFAULT_HEADER_HEIGHT;
  }, []);

  const updateIframeHeight = useCallback(() => {
    if (!isEmbed) return;

    const iframe = window.frameElement as HTMLIFrameElement | null;
    if (!iframe) return;

    const hasOpenElements = openElementsRef.current.size > 0;
    const isHovered = isHoveredRef.current;

    const shouldBeExpanded = isHovered || hasOpenElements;

    if (shouldBeExpanded) {
      iframe.style.height = FULL_SCREEN_HEADER_HEIGHT;
    } else {
      iframe.style.height = getCollapsedHeaderHeight();
    }
  }, [getCollapsedHeaderHeight, isEmbed]);

  // Updated to accept an array of items
  const registerOpenState = useCallback(
    (items: OpenStateItem[]) => {
      items.forEach((item) => {
        openElementsRef.current.set(item.id, item.close);
      });
      updateIframeHeight();
    },
    [updateIframeHeight],
  );

  // Updated to accept an array of IDs
  const unregisterOpenState = useCallback(
    (ids: string[]) => {
      ids.forEach((id) => {
        openElementsRef.current.delete(id);
      });
      updateIframeHeight();
    },
    [updateIframeHeight],
  );

  const closeAllOpenStates = useCallback(() => {
    openElementsRef.current.forEach((closeHandler) => {
      closeHandler?.();
    });
    //clear the map after triggering the handlers
    openElementsRef.current.clear();
    updateIframeHeight();
  }, [updateIframeHeight]);

  useEffect(() => {
    if (!isEmbed) return;

    const headerEl = headerRef.current;
    if (!headerEl) return;

    const handleMouseEnter = () => {
      isHoveredRef.current = true;
      updateIframeHeight();
    };

    const handleMouseLeave = () => {
      isHoveredRef.current = false;
      updateIframeHeight();
    };

    // Initialize height
    updateIframeHeight();

    headerEl.addEventListener('mouseenter', handleMouseEnter);
    headerEl.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      headerEl.removeEventListener('mouseenter', handleMouseEnter);
      headerEl.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isEmbed, updateIframeHeight]);

  const getPageLocation = (): PageLocation => {
    if (!isEmbed || typeof window === 'undefined')
      return {
        pathname: pathname || '',
        query: query || {},
      };

    try {
      const location = window.parent.location;
      const paramsObject = Object.fromEntries(
        new URLSearchParams(location.search).entries(),
      );
      return {
        pathname: location.pathname,
        query: paramsObject,
      };
    } catch {
      // Use iframe location if parent window is cross-origin
      return {
        pathname: window.location.pathname,
        query: Object.fromEntries(
          new URLSearchParams(window.location.search).entries(),
        ),
      };
    }
  };

  // Login URL adjusted for embed context
  const getLoginUrl = () => {
    if (!isEmbed || typeof window === 'undefined') return '/login';
    try {
      const redirectParam = encodeURIComponent(window.parent.location.pathname);
      return `/login?afterAuthRedirect=${redirectParam}`;
    } catch {
      // Use iframe location if parent window is cross-origin
      return `/login?afterAuthRedirect=${encodeURIComponent(window.location.pathname)}`;
    }
  };

  const reloadPage = () => {
    if (typeof window === 'undefined') return;
    if (isEmbed) {
      try {
        window.parent.location.reload();
      } catch {
        // Use iframe location if parent window is cross-origin
        window.location.reload();
      }
      return;
    }
    window.location.reload();
  };

  return (
    <EmbedContext.Provider
      value={{
        isInIframe: !!isEmbed,
        targetAttribute,
        headerRef,
        registerOpenState,
        unregisterOpenState,
        closeAllOpenStates,
        getPageLocation,
        navigateToRoute,
        getLoginUrl,
        reloadPage,
      }}
    >
      {children}
    </EmbedContext.Provider>
  );
};

/**
 * Hook to access Embed Context.
 */
export const useEmbedContext = (): EmbedContextProps =>
  useContext(EmbedContext);

/**
 * Hook to manage overlay state within an embedded context.
 */
export const useEmbedOverlay = ({ id, isOpen, onClose }: EmbedOverlayProps) => {
  const { registerOpenState, unregisterOpenState, isInIframe } =
    useEmbedContext();

  useEffect(() => {
    if (!isInIframe) return;

    if (isOpen) {
      // Register with the ID and the close handler
      registerOpenState([{ id, close: onClose }]);
    } else {
      unregisterOpenState([id]);
    }

    // Safety cleanup: ensure we unregister if the component unmounts
    return () => {
      unregisterOpenState([id]);
    };
  }, [isOpen, isInIframe, id, onClose, registerOpenState, unregisterOpenState]);
};
